import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"
import { useAuth } from "./useAuth"
import { useEnviarWhatsApp } from "./useWhatsApp"

type Package = Tables<"packages">
type Profile = Tables<"profiles"> & {
  condominium_id: string | null
}
type Unit = Tables<"units">
type Motoboy = Tables<"motoboys">

type PackageWithDetails = Package & {
  unit: Unit | null
  received_by_user: Profile | null
  motoboy: Motoboy | null
}

export const usePackages = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["packages", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("packages")
        .select(`
          *,
          unit:units(*),
          received_by_user:profiles!packages_received_by_fkey(*),
          motoboy:motoboys(*)
        `)
        .order("received_at", { ascending: false })

      if (error) throw error
      return data as PackageWithDetails[]
    },
    enabled: !!profile?.condominium_id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  })
}

// Nova função para moradores visualizarem encomendas da sua unidade
export const useResidentPackages = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["resident-packages", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return []

      console.log("Buscando encomendas para morador:", profile.id)
      console.log("Dados do perfil do morador:", {
        id: profile.id,
        apartment_number: profile.apartment_number,
        building: profile.building,
        condominium_id: profile.condominium_id,
        role: profile.role
      })

      // Primeiro, encontrar a unidade do morador
      // Priorizar busca por owner_id (mais confiável)
      let { data: units, error: unitError } = await supabase
        .from("units")
        .select("id, number, building, condominium_id, owner_id")
        .eq("owner_id", profile.id)

      console.log("Tentativa 1 - Busca por owner_id:", units?.length || 0, "unidades encontradas")

      // Se não encontrou por owner_id, tentar por apartment_number (sem building para evitar problemas com NULL)
      if ((!units || units.length === 0) && profile.apartment_number && profile.condominium_id) {
        const fallbackResult = await supabase
          .from("units")
          .select("id, number, building, condominium_id, owner_id")
          .eq("number", profile.apartment_number)
          .eq("condominium_id", profile.condominium_id)

        units = fallbackResult.data
        unitError = fallbackResult.error
        
        console.log("Tentativa 2 - Busca por apartment_number:", units?.length || 0, "unidades encontradas")
      }

      if (unitError) {
        console.error("Erro ao buscar unidade:", unitError)
        throw unitError
      }

      if (!units || units.length === 0) {
        console.log("Nenhuma unidade encontrada para o morador")
        return []
      }

      const unit = units[0]
      console.log("Unidade encontrada:", unit)

      // Buscar encomendas para a unidade
      const { data: packages, error: packagesError } = await supabase
        .from("packages")
        .select(`
          *,
          unit:units(*),
          received_by_user:profiles!packages_received_by_fkey(*)
        `)
        .eq("unit_id", unit.id)
        .order("received_at", { ascending: false })

      if (packagesError) {
        console.error("Erro ao buscar encomendas:", packagesError)
        throw packagesError
      }

      console.log("Encomendas encontradas:", packages?.length || 0)
      return packages as PackageWithDetails[] || []
    },
    enabled: !!profile?.id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  })
}

export const useCreatePackage = () => {
  const { profile } = useAuth()
  // envio automático pelo WhatsApp do condomínio (com fallback para o WhatsApp Web)
  const { enviar: enviarWhatsApp } = useEnviarWhatsApp()

  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (data: Omit<Package, "id" | "created_at" | "updated_at" | "unique_code"> & { whatsapp_phone?: string }) => {
      if (!profile?.condominium_id) throw new Error("Usuário sem condomínio associado")

      const { data: package_data, error } = await supabase
        .from("packages")
        .insert({
          ...data,
          received_by: profile.id
        })
        .select(`
          *,
          unit:units(*)
        `)
        .single()

      if (error) throw error

      console.log("Encomenda criada:", package_data)
      return { package_data, whatsapp_phone: data.whatsapp_phone }
    },
    onSuccess: async ({ package_data, whatsapp_phone }) => {
      queryClient.invalidateQueries({ queryKey: ["packages"] })
      queryClient.invalidateQueries({ queryKey: ["resident-packages"] })

      // Enviar WhatsApp se o telefone foi fornecido
      if (whatsapp_phone) {
        try {
          let message = `🏢 *NOVA ENCOMENDA REGISTRADA*

👤 *Destinatário:* ${package_data.recipient_name}
🏠 *Unidade:* ${package_data.unit?.number}
📦 *Código de Retirada:* ${package_data.unique_code}`

          if (package_data.description) {
            message += `\n📝 *Número do Pedido:* ${package_data.description}`
          }
          
          if (package_data.sender) {
            message += `\n🚚 *Empresa:* ${package_data.sender}`
          }
          
          if (package_data.photo_url) {
            message += `\n📸 *Foto da Encomenda:* ${package_data.photo_url}`
          }

          message += `\n\nPara retirar sua encomenda, apresente este código na portaria.

_Mensagem automática do sistema de portaria_`

          const cleanPhone = whatsapp_phone.replace(/\D/g, '')
          const formattedPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`

          // 1) tenta o envio automático pelo WhatsApp conectado do condomínio
          const resultado = await enviarWhatsApp({
            numeros: [formattedPhone],
            texto: message,
            origem: "encomenda",
            silencioso: true,
          })

          if (resultado.ok && resultado.enviados > 0) {
            toast.success("Morador avisado automaticamente pelo WhatsApp")
          } else {
            // 2) fallback: abre o WhatsApp Web para envio manual
            console.log("Envio automático indisponível — abrindo WhatsApp Web")
            window.open(`https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`, '_blank')
          }

        } catch (whatsappError) {
          console.error("Erro ao enviar WhatsApp:", whatsappError)
          toast.error("Erro ao enviar o aviso pelo WhatsApp")
        }
      }

      toast.success("Encomenda registrada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar encomenda:", error)
      toast.error("Erro ao registrar encomenda")
    }
  })
}

// Nova função para criar múltiplas encomendas e enviar WhatsApp agrupado
export const useCreateMultiplePackages = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (packageData: Array<Omit<Package, "id" | "created_at" | "updated_at" | "unique_code">>) => {
      if (!profile?.condominium_id) throw new Error("Usuário sem condomínio associado")

      const createdPackages = []

      // Criar todas as encomendas
      for (const singlePackage of packageData) {
        const { data: package_data, error } = await supabase
          .from("packages")
          .insert({
            ...singlePackage,
            received_by: profile.id
          })
          .select(`
            *,
            unit:units(*)
          `)
          .single()

        if (error) throw error
        createdPackages.push(package_data)
      }

      return { createdPackages, originalPackageData: packageData }
    },
    onSuccess: ({ createdPackages, originalPackageData }) => {
      queryClient.invalidateQueries({ queryKey: ["packages"] })
      queryClient.invalidateQueries({ queryKey: ["resident-packages"] })

      // Agrupar encomendas por número de WhatsApp
      const packagesByPhone: Record<string, Array<{ package_data: any, originalPackage: any }>> = {}
      
      createdPackages.forEach((package_data) => {
        const originalPackage = originalPackageData.find(p => p.recipient_name === package_data.recipient_name)
        const phone = originalPackage?.whatsapp_phone
        
        if (phone) {
          if (!packagesByPhone[phone]) {
            packagesByPhone[phone] = []
          }
          packagesByPhone[phone].push({ package_data, originalPackage })
        }
      })

      // Enviar WhatsApp agrupado por telefone
      Object.entries(packagesByPhone).forEach(([phone, packageGroup]) => {
        try {
          const firstPackage = packageGroup[0]
          
          let message = `🏢 *NOVA${packageGroup.length > 1 ? 'S' : ''} ENCOMENDA${packageGroup.length > 1 ? 'S' : ''} REGISTRADA${packageGroup.length > 1 ? 'S' : ''}*

👤 *Destinatário:* ${firstPackage.originalPackage.recipient_name}
🏠 *Unidade:* ${firstPackage.package_data.unit?.number}

📦 *Encomenda${packageGroup.length > 1 ? 's' : ''}:*`

          packageGroup.forEach((item, index) => {
            message += `\n\n*${index + 1}ª Encomenda:*`
            message += `\n📦 *Código de Retirada:* ${item.package_data.unique_code}`
            
            if (item.originalPackage.description) {
              message += `\n📝 *Número do Pedido:* ${item.originalPackage.description}`
            }
            
            if (item.originalPackage.sender) {
              message += `\n🚚 *Empresa:* ${item.originalPackage.sender}`
            }
            
            if (item.originalPackage.photo_url) {
              message += `\n📸 *Foto da Encomenda:* ${item.originalPackage.photo_url}`
            }
          })

          message += `\n\nPara retirar ${packageGroup.length > 1 ? 'suas encomendas' : 'sua encomenda'}, apresente ${packageGroup.length > 1 ? 'estes códigos' : 'este código'} na portaria.

_Mensagem automática do sistema de portaria_`

          const cleanPhone = phone.replace(/\D/g, '')
          const formattedPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`
          
          const whatsappLink = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`
          
          console.log("Abrindo WhatsApp Web com mensagem agrupada:", message)
          console.log("Total de encomendas na mensagem:", packageGroup.length)
          
          window.open(whatsappLink, '_blank')
          
        } catch (whatsappError) {
          console.error("Erro ao preparar WhatsApp Web agrupado:", whatsappError)
          toast.error("Erro ao preparar WhatsApp Web")
        }
      })

      toast.success(`${createdPackages.length} encomenda(s) registrada(s) com sucesso!`)
    },
    onError: (error) => {
      console.error("Erro ao registrar encomendas:", error)
      toast.error("Erro ao registrar encomendas")
    }
  })
}

export const useUpdatePackageStatus = () => {
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      delivered_at, 
      delivered_to 
    }: { 
      id: string; 
      status: string; 
      delivered_at?: string;
      delivered_to?: string;
    }) => {
      const updateData: any = { status, delivered_at }
      if (delivered_to) {
        updateData.notes = `Retirado por: ${delivered_to}`
      }

      const { data, error } = await supabase
        .from("packages")
        .update(updateData)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] })
      queryClient.invalidateQueries({ queryKey: ["resident-packages"] })
      toast.success("Status da encomenda atualizado!")
    },
    onError: (error) => {
      console.error("Erro detalhado ao atualizar status:", error)
      toast.error(`Erro ao atualizar status: ${error.message}`)
    }
  })
}
